import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getMiscBillById } from "../../api/Api";
import logo from "../../Assets/Kalam Kids Logo_newlogo.png";

const MiscBillView = ({ billId, bill: passedBill, onBack }) => {
  const [bill, setBill] = useState(passedBill || null);
  const [loading, setLoading] = useState(!passedBill);

  useEffect(() => {
    const fetchBillDetails = async () => {
      if (passedBill) {
        setBill(passedBill);
        setLoading(false);
        return;
      }
      try {
        const response = await getMiscBillById(billId);
        setBill(response.data);
      } catch (err) {
        toast.error("Failed to load bill details.");
      } finally {
        setLoading(false);
      }
    };
    fetchBillDetails();
  }, [billId, passedBill]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) return <div className="p-6 text-center">Loading...</div>;
  if (!bill) return <div className="p-6 text-center">Bill not found</div>;

  return (
    <div
      className="max-w-4xl mx-auto bg-white p-8 font-sans text-gray-800"
      id="misc-bill-printable-content"
    >
      {/* Print Styles */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #misc-bill-printable-content, #misc-bill-printable-content * {
              visibility: visible;
            }
            #misc-bill-printable-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 20px;
            }
            .print\\:hidden {
              display: none !important;
            }
          }
        `}
      </style>

      {/* Header Section */}
      <div className="flex justify-between items-start mb-6 border-b border-gray-100 pb-4">
        <div>
          <img src={logo} alt="Kalam Kids" className="w-32 mb-1" />
        </div>
        <div className="text-right">
          <h1 className="text-2xl font-bold text-gray-900">Cash Receipt</h1>
          <p className="text-gray-500 text-xs mt-1">
            Generated on {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Compact Info Grid - 4 Columns to save vertical space */}
      <div className="grid grid-cols-4 gap-x-4 gap-y-4 mb-8 bg-gray-50 p-4 rounded-sm border border-gray-100">
        {/* Row 1: Key Voucher Info */}
        <div>
          <label className="block text-[#16bab1] text-xs font-bold uppercase tracking-wider mb-1">
            Invoice No
          </label>
          <div className="font-bold text-gray-900 text-sm">
            {bill.invoiceNo}
          </div>
        </div>

        <div>
          <label className="block text-[#16bab1] text-xs font-bold uppercase tracking-wider mb-1">
            Date
          </label>
          <div className="font-bold text-gray-900 text-sm">
            {new Date(bill.date).toLocaleDateString()}
          </div>
        </div>

        <div>
          <label className="block text-[#16bab1] text-xs font-bold uppercase tracking-wider mb-1">
            Payment Mode
          </label>
          <div className="font-bold text-gray-900 text-sm">
            {bill.modeOfPayment}
          </div>
        </div>

        <div>
          <label className="block text-[#16bab1] text-xs font-bold uppercase tracking-wider mb-1">
            Approved
          </label>
          <div className="font-bold text-gray-900 text-sm">Yes</div>
        </div>

        {/* Row 2: Payee Info */}
        <div className="col-span-1">
          <label className="block text-[#16bab1] text-xs font-bold uppercase tracking-wider mb-1">
            Payee Name
          </label>
          <div
            className="font-bold text-gray-900 text-sm truncate"
            title={bill.payeeName}
          >
            {bill.payeeName || "N/A"}
          </div>
        </div>

        <div className="col-span-1">
          <label className="block text-[#16bab1] text-xs font-bold uppercase tracking-wider mb-1">
            Phone
          </label>
          <div className="font-bold text-gray-900 text-sm">
            {bill.payeePhoneNumber || "N/A"}
          </div>
        </div>

        <div className="col-span-1">
          <label className="block text-[#16bab1] text-xs font-bold uppercase tracking-wider mb-1">
            Category
          </label>
          <div
            className="font-bold text-gray-900 text-sm truncate"
            title={bill.category?.name}
          >
            {bill.category?.name || "N/A"}
          </div>
        </div>

        <div className="col-span-1">
          {bill.studentId && (
            <>
              <label className="block text-[#16bab1] text-xs font-bold uppercase tracking-wider mb-1">
                Student
              </label>
              <div className="font-bold text-gray-900 text-sm truncate">
                {bill.studentId.studentName}
              </div>
            </>
          )}
        </div>

        {/* Row 3: Particulars (Full Width) */}
        <div className="col-span-4 border-t border-gray-200 pt-3 mt-1">
          <label className="block text-[#16bab1] text-xs font-bold uppercase tracking-wider mb-1">
            Particulars
          </label>
          <div className="font-bold text-gray-900 text-sm">
            {bill.particulars || "N/A"}
          </div>
        </div>
      </div>

      {/* Items Table - Taking up remaining space */}
      <div className="mb-6">
        <div className="flex justify-between items-center border-b-2 border-gray-100 pb-2 mb-3">
          <h3 className="font-bold text-gray-800 text-sm uppercase">
            Description
          </h3>
          <h3 className="font-bold text-gray-800 text-sm uppercase">Amount</h3>
        </div>

        <div className="space-y-3 min-h-[100px]">
          {bill.items.map((item, index) => (
            <div
              key={index}
              className="flex justify-between items-start text-sm"
            >
              <div className="text-gray-700 font-medium">
                {item.description}
              </div>
              <div className="font-bold text-gray-900">
                ₹{item.amount.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Totals Section */}
      <div className="border-t border-gray-200 pt-4 mb-8">
        <div className="flex justify-end items-center mb-6">
          <span className="font-bold text-gray-900 text-base mr-8">
            Total Amount
          </span>
          <span className="font-bold text-gray-900 text-xl">
            ₹{bill.totalAmount.toLocaleString()}
          </span>
        </div>

        <div className="mb-8">
          <label className="block text-gray-500 text-xs font-bold mb-1 uppercase">
            Amount in words
          </label>
          <div className="italic text-gray-800 font-medium capitalize text-sm">
            {bill.amountInWords} Only
          </div>
        </div>
      </div>

      {/* Footer Details */}
      <div className="flex justify-between items-end mt-auto pt-4 border-t border-gray-200">
        <div>
          <label className="block text-gray-500 text-xs font-bold mb-2 uppercase">
            Accountant
          </label>
          <div className="font-bold text-gray-900 text-base">
            {bill.accountantName || "N/A"}
          </div>
        </div>

        <div className="text-right text-xs">
          <a
            href="http://www.kalamkids.in"
            className="text-[#16bab1] font-bold hover:underline block mb-1"
          >
            www.kalamkids.in
          </a>
          {bill.branch && bill.branch.address && (
            <p className="text-gray-500 leading-relaxed">
              {bill.branch.address}
            </p>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-4 mt-8 print:hidden">
        <button
          onClick={onBack}
          className="px-6 py-2 bg-gray-500 text-white font-medium rounded hover:bg-gray-600 transition-colors text-sm"
        >
          Back
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-6 py-2 bg-[#16bab1] text-white font-medium rounded hover:bg-[#139c94] transition-colors text-sm"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
            />
          </svg>
          Print Voucher
        </button>
      </div>
    </div>
  );
};

export default MiscBillView;
